import { useAtomValue } from 'jotai';
import { useState } from 'react';
import { ChildIcon, UnisexIcon } from '@/components/Icons';
import { Optional } from '@/shared/Optional';
import { R } from '@/features/booking/resources/AddChildrenModal.resx';
import {
    IAddHouseholdMemberRequest,
    PatientHouseholdMemberDto,
} from '@/shared/interfaces';
import { useAddChild } from '@/features/shared/features/addOrEditChild/lib/useAddChild';
import { unassociatedChildrenAtom } from '@/features/shared/features/addOrEditChild/model/unassociatedChildrenAtom';
import { HouseholdMemberType } from '@/shared/enums';
import {
    adolescentMinimumAgeAtom,
    adultMinimumAgeAtom,
} from '@/caregiverSpace/shared/model';

export enum AddChildrenMode {
    Booking,
    Profile,
    Newborn,
}

export type AddChildrenModalOptions = {
    allowedTypes: HouseholdMemberType[];
    mode: AddChildrenMode;
    onSubmitAdolescent?: (data: IAddHouseholdMemberRequest) => Promise<unknown>;
    onChildAdded: (uuid: Optional<string>) => void;
    onClose: () => void;
};

export function useAddChildren({
    allowedTypes,
    mode,
    onSubmitAdolescent,
    onClose,
    onChildAdded,
}: AddChildrenModalOptions) {
    const adolescentMinimumAge = useAtomValue(adolescentMinimumAgeAtom);
    const adultMinimumAge = useAtomValue(adultMinimumAgeAtom);

    const [showManualCreateChild, setShowManualCreateChild] = useState(false);
    const [showAdolescentModal, setShowAdolescentModal] = useState(false);
    const [adolescentToAdd, setAdolescentToAdd] =
        useState<PatientHouseholdMemberDto | null>(null);
    const addChild = useAddChild();
    const unassociatedChildren = useAtomValue(unassociatedChildrenAtom);
    const [selectedChild, setSelectedChild] =
        useState<PatientHouseholdMemberDto | null>(null);

    const children =
        unassociatedChildren?.data?.filter((child) =>
            allowedTypes.includes(child.Type),
        ) ?? [];

    const isAdolescentOnly =
        mode === AddChildrenMode.Profile &&
        allowedTypes.length === 1 &&
        allowedTypes.includes(HouseholdMemberType.Adolescent);

    if (mode === AddChildrenMode.Profile && allowedTypes.length > 1) {
        throw new Error(
            'AddChildrenModal: multiple household member types are not supported in Profile mode.',
        );
    }
    if (
        isAdolescentOnly &&
        (adultMinimumAge === undefined || onSubmitAdolescent === undefined)
    ) {
        throw new Error(
            'AddChildrenModal: adultMinimumAge and onSubmitAdolescent are required when this modal is only showing adolescents.',
        );
    }

    const onChildClick = (child: PatientHouseholdMemberDto) => {
        if (child.Type === HouseholdMemberType.Adolescent) {
            setAdolescentToAdd(child);
            setShowAdolescentModal(true);
            return;
        }
        setSelectedChild(child);
    };

    const onClickCreateManually = () => {
        if (unassociatedChildren.fetchStatus === 'fetching') return;

        if (isAdolescentOnly) {
            setAdolescentToAdd(null);
            setShowAdolescentModal(true);
            return;
        }
        setShowManualCreateChild(true);
    };

    const getChildIcon = (child: PatientHouseholdMemberDto) =>
        child.Type === HouseholdMemberType.Adolescent ? (
            <UnisexIcon className="icon-box" />
        ) : (
            <ChildIcon className="icon-box" />
        );

    const createManuallyCaption = isAdolescentOnly
        ? R.buttonInviteManually
        : R.buttonCreateManually;

    const onCloseChildModal = () => {
        setShowManualCreateChild(false);
        setSelectedChild(null);
    };

    const onMutationSuccess = () => onChildAdded(null);

    const onCloseAdolescentModal = (shouldRefetch?: boolean) => {
        setShowAdolescentModal(false);
        setAdolescentToAdd(null);

        if (shouldRefetch) {
            onChildAdded(null);
            onClose();
            return;
        }
        onClose();
    };

    return {
        children,
        addChild,
        onChildClick,
        onClickCreateManually,
        getChildIcon,
        createManuallyCaption,
        showChildModal: showManualCreateChild || selectedChild !== null,
        prefilledChild: selectedChild
            ? {
                  name: selectedChild.FullName,
                  cpr: selectedChild.CprNumber,
              }
            : undefined,
        onCloseChildModal,
        onMutationSuccess,
        showAdolescentModal:
            showAdolescentModal &&
            adolescentMinimumAge !== undefined &&
            adultMinimumAge !== undefined,
        adolescentInitialValues: adolescentToAdd
            ? {
                  Name: adolescentToAdd.FullName,
                  Cpr: adolescentToAdd.CprNumber,
              }
            : undefined,
        onCloseAdolescentModal,
        setSelectedChild,
    };
}
